// Frameworks/components/HexMapCanvas.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const SQRT3 = Math.sqrt(3);

// ---- math helpers ----
function axialToPixel([q, r], size) {
  const x = size * (SQRT3 * q + (SQRT3 / 2) * r);
  const y = size * (3 / 2) * r;
  return [x, y];
}
function hexPoints(size) {
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30);
    pts.push([size * Math.cos(angle), size * Math.sin(angle)]);
  }
  return pts.map((p) => p.join(",")).join(" ");
}
// stable deterministic "random" in [0,1) from (q,r)
function seeded(q, r) {
  let h = (q * 1836311903) ^ (r * 2971215073);
  h ^= h >>> 16;
  h = (h * 2246822519) >>> 0;
  return (h % 1000) / 1000;
}

export default function HexMapCanvas({ graph }) {
  const ref = useRef(null);

  // --- Keep navbar fully opaque: render the map starting *below* it ---
  const [headerTop, setHeaderTop] = useState(0);
  useEffect(() => {
    const computeHeaderTop = () => {
      // Prefer a top header/nav; fall back to tallest fixed/sticky element at page top.
      let h = 0;
      const candidates = document.querySelectorAll("header, nav, .navbar, .site-nav");
      if (candidates.length) {
        for (const el of candidates) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 0) h = Math.max(h, rect.height || 0);
        }
      } else {
        // generic scan for any fixed/sticky bar at the top
        for (const el of Array.from(document.body.children)) {
          const cs = window.getComputedStyle(el);
          if ((cs.position === "fixed" || cs.position === "sticky") && el.getBoundingClientRect().top <= 0) {
            h = Math.max(h, el.getBoundingClientRect().height || 0);
          }
        }
      }
      setHeaderTop(h);
    };
    computeHeaderTop();
    window.addEventListener("resize", computeHeaderTop);
    return () => window.removeEventListener("resize", computeHeaderTop);
  }, []);

  // maintain viewport-sized canvas *below* the header
  const [dims, setDims] = useState({
    w: window.innerWidth,
    h: Math.max(window.innerHeight - 0, 360),
  });
  useEffect(() => {
    const onResize = () =>
      setDims({
        w: window.innerWidth,
        h: Math.max(window.innerHeight - headerTop, 360),
      });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [headerTop]);

  // Visual hex radius (UI)
  const size = 44;

  // Background coords (big honeycomb field). We keep your approach intact.
  const bgCols = useMemo(() => {
    const cols = [];
    const range = 20;
    for (let q = -range; q <= range; q++) {
      for (let r = -range; r <= range; r++) {
        const s = -q - r;
        if (Math.abs(s) > range) continue;
        cols.push([q, r]);
      }
    }
    return cols;
  }, []);

  const points = useMemo(() => hexPoints(size - 2), [size]);

  const current = graph?.current || [0, 0];
  const visitedSet = graph?.visited || new Set();
  const candidates = Array.isArray(graph?.candidates) ? graph.candidates : []; // chips-only

  // Center on current (use dims so centering respects header offset)
  const [cx, cy] = axialToPixel(current, size);
  const tx = dims.w / 2 - cx;
  const ty = dims.h / 2 - cy;

  // Palette
  const CURRENT = "#FFBF00";
  const VISITED = "#1fb6a6"; // brand teal
  const CANDIDATE = "#6E56CF"; // branching paths (only when chips are shown)
  const GHOST = "#10243A"; // base (we vary opacity)

  return (
    <div
      ref={ref}
      className="hexmap-canvas"
      style={{
        position: "fixed",
        top: "0px", // match your navbar height
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        background: "transparent",
        pointerEvents: "none",
      }}
    >
      <svg
        className="hex-grid-svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ display: "block", background: "transparent" }}
      >
        <g
          transform={`translate(${tx},${ty})`}
          style={{
            transition: "transform 1000ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
          }}
        >
          {bgCols.map((c, idx) => {
            const [x, y] = axialToPixel(c, size);
            const k = `${c[0]},${c[1]}`;
            const noise = seeded(c[0], c[1]);

            // small fraction dropped to avoid perfect regularity
            if (noise < 0.045) return null;

            // defaults for ghost tiles
            let fill = GHOST;
            let stroke = "transparent";
            let opacity = 0.04 + 0.06 * noise;

            // visited/current/candidates overrides
            if (visitedSet.has && visitedSet.has(k)) {
              fill = VISITED;
              opacity = 0.95;
            }
            if (candidates.find((cc) => cc[0] === c[0] && cc[1] === c[1])) {
              fill = CANDIDATE;
              opacity = 0.95;
            }
            if (current[0] === c[0] && current[1] === c[1]) {
              fill = CURRENT;
              stroke = "#f2c14e";
              opacity = 0.98;
            }

            return (
              <polygon
                key={idx}
                points={points}
                transform={`translate(${x},${y})`}
                fill={fill}
                stroke={stroke}
                strokeWidth={
                  current[0] === c[0] && current[1] === c[1] ? 2 : 0
                }
                opacity={opacity}
                style={{
                  transition:
                    "fill 1000ms ease, opacity 1000ms ease, stroke 1000ms ease",
                  willChange: "fill, opacity, stroke",
                }}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
}
